/**
 * Explore Pie view (POS2-884): donut of the split's window totals — the window total centred in
 * the hole, a legend of value + % share per slice. Slices/legend ordered by total desc; the
 * remainder is a slice so the shares sum to 100%. Only rendered with a split (the shell's
 * resolveView falls back to Line when Split = none).
 */
import { Cell, Pie, PieChart } from "recharts";

import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { useTranslation } from "@/i18n/use-translation";
import { EXPLORE_MEASURES } from "@/lib/explore-engine";
import { buildPieData, type PieSlice } from "@/lib/explore-chart";

import type { ExploreViewProps } from "./chart-parts";

export function ExplorePie({ pivot, measure, currency }: ExploreViewProps) {
  const { locale, t } = useTranslation();
  const fmt = (n: number) => EXPLORE_MEASURES[measure].format(n, { currency, locale, t });
  const { slices, total } = buildPieData(pivot, measure);
  const config: ChartConfig = Object.fromEntries(
    slices.map((s) => [s.key, { label: s.label, color: s.color }]),
  );

  return (
    <div
      className="flex h-[420px] flex-col items-center justify-center gap-6 sm:flex-row sm:gap-10"
      data-testid="explore-view-pie"
    >
      <div className="relative aspect-square h-full max-h-[380px] w-auto min-w-0">
        <ChartContainer config={config} className="aspect-square h-full w-full">
          <PieChart>
            <ChartTooltip content={<PieTooltip slices={slices} fmt={fmt} />} />
            <Pie
              data={slices}
              dataKey="value"
              nameKey="key"
              innerRadius="62%"
              outerRadius="92%"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {slices.map((s) => (
                <Cell key={s.key} fill={s.color} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold tabular-nums text-gray-900">{fmt(total)}</span>
        </div>
      </div>

      <div
        className="flex max-h-full min-w-0 flex-col gap-1.5 overflow-auto text-xs"
        data-testid="explore-legend"
      >
        {slices.map((s) => (
          <div key={s.key} className="flex items-center gap-2">
            <span className="size-2.5 shrink-0 rounded-[2px]" style={{ background: s.color }} />
            <span className="min-w-0 flex-1 truncate text-gray-600" title={s.label}>
              {s.label}
            </span>
            <span className="tabular-nums font-medium text-gray-900">{fmt(s.value)}</span>
            <span className="w-9 text-right tabular-nums text-gray-400">
              {Math.round(s.share * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

type TooltipItem = { name?: string | number; value?: number };

function PieTooltip({
  active,
  payload,
  slices,
  fmt,
}: {
  active?: boolean;
  payload?: TooltipItem[];
  slices: PieSlice[];
  fmt: (n: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const s = slices.find((x) => x.key === String(item.name));
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-lg">
      <span className="flex items-center gap-1.5">
        <span
          className="size-2 shrink-0 rounded-[2px]"
          style={{ background: s?.color ?? "#9ca3af" }}
        />
        <span className="text-gray-600">{s?.label ?? String(item.name)}</span>
        <span className="font-medium tabular-nums text-gray-900">
          {fmt(Number(item.value ?? 0))}
        </span>
        <span className="tabular-nums text-gray-400">· {Math.round((s?.share ?? 0) * 100)}%</span>
      </span>
    </div>
  );
}
